import { useCallback, useEffect, useState } from 'react';
import { connect } from 'redux-bundler-react';
import StepHeader from '@components/step-header/StepHeader';
import { Button, Alert } from '@trussworks/react-uswds';
import DatePicker from '@components/date-picker/DatePicker';
import TextInput from '@components/text-input/TextInput';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Section from '@components/section/Section';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { filterNullEmptyObjects, doClearIncompletePhoneInfo } from '@src/utils/helpers';
import { useRequest, useUserRoleCapabilities } from '@stores';
import { useResetDirtyFields, useUserProfile } from '@hooks';
import { getRequestOwner } from '@api/services/authorized-user/helper';
import { AuthorizedUserPermissions } from '@api/services/authorized-user/types';
import { RequestStatus, RequestType } from '@src/utils/enums';
import useGetAuthorizedUsers from '@api/services/authorized-user/hooks/useGetAuthorizedUsers';
import { useLookupTableFormVersions } from '@api/services/lookup/hooks';
import CertifySignSubmitRoleAlert from '@forms/input-forms/common/CertifySignSubmitRoleAlert';

export const PreAppForm3CertifySignSubmitMetadata = {
  sectionName: 'Certify, Sign, and Submit',
};

const PreAppForm3CertifySignSubmit = connect(
  'doUpdateRequestObject',
  'doUploadAndUpdatePreApp',
  'selectRequestAPIData',
  'selectRequestFormData',
  'selectPreAppErrors',
  'selectRequestID',
  'selectRequestStatus',
  ({
    doUpdateRequestObject,
    doUploadAndUpdatePreApp,
    requestAPIData,
    requestFormData,
    preAppErrors,
    stepNo,
    requestID,
    version,
    requestStatus,
  }) => {
    const { data: authorizedUsersData } = useGetAuthorizedUsers({ requestId: requestID, version });

    const { userProfileData } = useUserProfile();
    const canSign = useUserRoleCapabilities((state) => state.roleCapabilities.signSubmitRequest);
    const lookupTableFormVersions = useLookupTableFormVersions();
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched, selectedRequestId, sections } = useRequest();

    const authorizedUsers = authorizedUsersData ?? [];
    const requestOwner = getRequestOwner(authorizedUsers);
    const isRequestOwner = requestAPIData?.request?.permission === AuthorizedUserPermissions.Owner;
    const canUserSign = canSign && isRequestOwner;
    const hasSubmitted = requestStatus !== RequestStatus.Draft;
    const formVersion = lookupTableFormVersions.find(
      (item) => item.requestType === RequestType.PreApplication
    )?.formVersion;

    const getName = useCallback(
      () =>
        userProfileData?.middleName
          ? `${userProfileData.firstName} ${userProfileData.middleName} ${userProfileData.lastName}`
          : `${userProfileData?.firstName} ${userProfileData?.lastName}`,
      [userProfileData?.firstName, userProfileData?.middleName, userProfileData?.lastName]
    );

    const defaultValues = {
      signature: requestAPIData?.request?.signature ?? null,
      signatureDate:
        requestAPIData?.request?.signatureDate !== null
          ? requestAPIData?.request?.signatureDate?.split('T')[0]
          : ('' ?? ''),
    };

    const [isSigned, setIsSigned] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    const schema = yup.object().shape({
      signature: yup.string().test('isSigned', `Signature must be ${getName()}`, (val) => val.trim() === getName()),
      signatureDate: yup.string().required('Please select a date'),
    });

    const methods = useForm({ defaultValues: defaultValues, resolver: yupResolver(schema), mode: 'onBlur' });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      handleSubmit,
      trigger,
      reset,
    } = methods;

    const isTouched = isSectionTouched(stepNo);
    const isDirty = Object.keys(dirtyFields).length > 0;
    const errorCount = Object.keys(errors).length;
    const signature = watch('signature');
    const signatureDate = watch('signatureDate');
    const dateNow = new Date().toISOString().slice(0, 10);

    //Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    const handleBlur = (e) => {
      setValue('signature', e.target.value.trim());
    };

    useEffect(() => {
      if (signature?.trim() === getName()) {
        !signatureDate && setValue('signatureDate', new Date().toISOString().slice(0, 10));
        setIsSigned(true);
        trigger();
      } else {
        setIsSigned(false);
      }
    }, [signature, getName, setValue, trigger, signatureDate]);

    const submitForm = () => {
      //require submission endpoint would be called here
      const requestFormDataSubmit = {
        ...requestFormData,
        request: { ...requestFormData.request, isSubmit: true, formVersion: formVersion },
      };
      const cleanedPayload = doClearIncompletePhoneInfo(requestFormDataSubmit);
      const filteredRequest = filterNullEmptyObjects(cleanedPayload);
      doUploadAndUpdatePreApp(filteredRequest, 'Submit');
    };

    // Consolidated effect: Calculate form validity and update section state
    useEffect(() => {
      const hasInvalidSections = sections.some(
        (section) => section.valid === false && section.label !== 'Certify, Sign, and Submit'
      );
      const newIsFormValid = !hasInvalidSections;

      // Only update if state actually changed to prevent cascading re-renders
      if (newIsFormValid !== isFormValid) {
        setIsFormValid(newIsFormValid);
      }

      // Update section with all props at once
      updateSection(stepNo, {
        dirty: isDirty,
        valid: newIsFormValid && isSigned,
      });

      // Only update request on successful completion
      if (newIsFormValid && isSigned && signature && signatureDate) {
        doUpdateRequestObject({ signature, signatureDate });
      }
    }, [
      stepNo,
      isDirty,
      isFormValid,
      isSigned,
      signature,
      signatureDate,
      sections,
      doUpdateRequestObject,
      updateSection,
    ]);

    const renderSignatureSection = () => {
      if (!isFormValid) {
        return (
          <Alert slim className='margin-top-3' type='error'>
            In order to submit this form, please revisit the section(s) marked with errors, and correct any missing or
            invalid fields.
          </Alert>
        );
      }

      return (
        <Section label='Signature'>
          <div className='grid-row grid-gap-1'>
            <div className='grid-col-6'>
              <TextInput
                name='signature'
                label='Signature'
                signature={canUserSign && getName()}
                required
                readOnly={isReadOnly || !canUserSign}
                onBlur={handleBlur}
              />
            </div>
            <div className='grid-col-6'>
              <DatePicker
                min={null}
                max={dateNow}
                name='signatureDate'
                label='Signature Date'
                readOnly={isReadOnly || !canUserSign}
                required
              />
            </div>
          </div>
          <hr />
          <div className='display-flex flex-justify-start padding-top-1'>
            <Button
              className='submit-request-button'
              title='Submit Request'
              disabled={!isSigned || isReadOnly || !canUserSign}
              onClick={handleSubmit(submitForm)}
            >
              Submit Request
            </Button>
          </div>
        </Section>
      );
    };

    return (
      <FormProvider {...methods}>
        {(!isFormValid || errorCount > 0) && !isReadOnly && <ErrorSummary errors={errors} type='form' />}
        {preAppErrors && Object.keys(preAppErrors)?.length > 0 && (
          <ErrorSummary errors={preAppErrors} type='api-validation' />
        )}
        <CertifySignSubmitRoleAlert showAlert={!hasSubmitted && !canSign} />
        {!isRequestOwner && !hasSubmitted && (
          <Alert slim className='margin-top-3 margin-bottom-3' type='warning'>
            You are not authorized to Certify, Sign and Submit this request. Only the{' '}
            <span className='text-bold'>
              Request Owner: {requestOwner?.firstName} {requestOwner?.lastName}
            </span>{' '}
            can perform this operation.
          </Alert>
        )}
        <StepHeader text='Certify, Sign, and Submit'>
          <span className='text-bold'>
            This request must be signed by the person who desires to undertake the proposed activity (applicant) or it
            may be signed by a duly authorized agent if a completed agent authorization is provided.
          </span>
        </StepHeader>
        <h6>BY SUBMITTING THIS REQUEST, I AM AGREEING TO ALL OF THE FOLLOWING:</h6>
        <ul className='padding-left-3 text-bold'>
          <li>
            I consent to conduct business electronically and understand this consent can be withdrawn at any time in
            writing to the local USACE Regulatory office.
          </li>
          <li>
            By signing this request, I certify that the information contained in this request and any attached documents
            are true and correct to the best of my knowledge.
          </li>
          {selectedRequestId === '1' && (
            <li>
              I understand that, as an agent, I am providing this information on behalf of my client on a voluntary
              basis to help facilitate a more efficient review of the project.
            </li>
          )}
          <li>
            I understand that, whoever, in any matter within the jurisdiction of any department of agency of the United
            States, knowingly and willfully (1) falsifies, conceals, or covers up by any trick, scheme, or device a
            material fact; (2) makes any materially false, fictitious, or fraudulent statement or representation; or (3)
            makes or uses any false writing or document knowing the same to contain any materially false, fictitious, or
            fraudulent statement or entry; shall be fined not more than $10,000 or imprisoned not more than 5 years or
            both. (18 USC Section 1001)
          </li>
          <li>
            I understand that by typing my name in the text field below that I am e-signing this application and I
            understand that this e-signature has the same legal status as handwritten signatures.
          </li>
        </ul>

        {renderSignatureSection()}
      </FormProvider>
    );
  }
);

PreAppForm3CertifySignSubmit.metadata = PreAppForm3CertifySignSubmitMetadata;

export default PreAppForm3CertifySignSubmit;
